import React, { useEffect, useState } from "react";
import ApiInterface from "@/commons/api/ApiInterface";
import { decoded } from "@/commons/base64/Decoder";
import CloudImage from "@/commons/components/image/CloudImage";
import styles from "./Comment.module.scss";
import CustomizedTooltip from "@/commons/utils/toolpit/CustomizedTooltip";

interface CommentData {
    id: string;
    content: string;
    time: string;
    userId: {
        id: string;
        username: string;
        staffGroup: {
            iconUUID: string;
            groupName: string;
            id: number;
        };
    };
}

interface CommentProps {
    commentJson: CommentData;
    asComment?: boolean;
    onClickUsername?: (userId: string, event: React.MouseEvent<HTMLDivElement>) => void;
}

const Comment: React.FC<CommentProps> = ({
                                             commentJson,
                                             asComment = true,
                                             onClickUsername = () => {}
                                         }) => {
    const [imageUrl, setImageUrl] = useState("");
    const [content, setContent] = useState<React.ReactNode>(null);

    useEffect(() => {
        try {
            const decodedContent = decoded(commentJson.content);
            setContent(createReactContent(decodedContent));
        } catch {
            setContent(createReactContent(commentJson.content));
        }
    }, [commentJson]);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    function buildLink(el: HTMLElement, children: React.ReactNode) {
        const href = el.getAttribute("href") || "#";
        return (
            <CustomizedTooltip
                tooltipContent={
                    <>
                        <span style={{ color: "#4bffff" }}>Apre il link:&nbsp;</span>
                        <span style={{ color: "#70a2ff" }}>{href}</span>
                    </>
                }
            >
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                >
                    {children}
                </a>
            </CustomizedTooltip>
        );
    }

    const createReactContent = (text: string): React.ReactNode => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");

        const transformNode = (node: ChildNode, key: number): React.ReactNode => {
            if (node.nodeType === Node.TEXT_NODE) {
                const content = node.textContent || "";
                return colorizeText(content);
            }

            if (node.nodeType !== Node.ELEMENT_NODE) return null;

            const el = node as HTMLElement;
            const children = Array.from(el.childNodes).map((child, i) => transformNode(child, i));

            switch (el.tagName.toLowerCase()) {
                case "img": {
                    const src = el.getAttribute("src");
                    if (src) return <CloudImage key={key} uuid={src} enableFullscreen={true} />;
                    return null;
                }
                case "a":
                    return buildLink(el, children);
                case "b":
                    return <b key={key}>{children}</b>;
                case "i":
                    return <i key={key}>{children}</i>;
                case "u":
                    return <u key={key}>{children}</u>;
                case "p":
                    return <p key={key}>{children}</p>;
                case "div":
                    return <div key={key}>{children}</div>;
                case "br":
                    return <br key={key} />;
                case "span":
                    return <span key={key}>{children}</span>;
                default:
                    return <span key={key}>{children}</span>;
            }
        };

        return Array.from(doc.body.childNodes).map((node, i) => transformNode(node, i));
    };

    const colorizeText = (text: string): React.ReactNode => {
        if (commentJson.userId.username === "SISTEMA") {
            if (text.toLowerCase().includes("chiuso")) {
                return <span style={{ color: "#ff0000" }}>{text}</span>;
            } else if (text.toLowerCase().includes("categoria aggiornata")) {
                return <span style={{ color: "#00a8ff" }}>{text}</span>;
            } else if (text.toLowerCase().includes("->")) {
                let before = text.split("->")[0];
                let after = text.split("->")[1];
                return (
                    <>
                        <b><span style={{ color: "#ff4848" }}>{before}</span></b>
                        <span style={{ color: "#00a8ff" }}>{"⇒"}</span>
                        <b><span style={{ color: "#91ff56" }}>{after}</span></b>
                    </>
                );
            } else if (text.toLowerCase().includes("abbiamo ricevuto il tuo report")) {
                return <span style={{ color: "#ffffff" }}>{text}</span>;
            } else if (text.toLowerCase().includes("messaggio automatico")) {
                return <span style={{ color: "#c2c2c2" }}>{text}</span>;
            }
            return <span style={{ color: "#65c400" }}>{text}</span>;
        }
        return text;
    };

    // ✅ Return alternativo se `asComment` è false
    if (!asComment) return <>{content}</>;

    return (
        <div className={styles.post_comment} id={`comment_${commentJson.id}`}>
            <div
                className={styles.post_comment_header}
                onClick={e => onClickUsername(commentJson.userId.id, e)}
            >
                {commentJson.userId.username}
                <CloudImage uuid={commentJson.userId.staffGroup.iconUUID} />
            </div>
            <div className={styles.post_comment_body}>{content}</div>
            <div className={styles.post_comment_date}>{formatDate(commentJson.time)}</div>
            <div className={styles.post_comment_separator} />
        </div>
    );
};

export default Comment;